import {
  formatBytes,
  formatStoragePercent,
  STORAGE_LIMIT_BYTES,
  storageUsedPercent,
  useDismissOnOutside,
  useStorageUsage,
} from '@/shared/utils'
import React, {useCallback, useRef, useState} from 'react'
import './storage-usage-dropdown.scss'

const MENU_ID = 'omics-files-usage-menu'

/** Used, limit and free space, with a bar; the body of the Storage box. */
const UsageDetails: React.FC<{usage: number}> = ({usage}) => {
  const percent = storageUsedPercent(usage)
  const percentLabel = formatStoragePercent(usage)
  return (
    <div className='ob-storage-usage__body' data-testid='omics-files-usage-details'>
      <div className='ob-storage-usage__used'>
        <span className='ob-storage-usage__value'>{formatBytes(usage)}</span> used
      </div>
      <div
        className='ob-storage-usage__bar'
        role='progressbar'
        aria-label='Storage used'
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`${percentLabel} of ${formatBytes(STORAGE_LIMIT_BYTES)}`}
      >
        <div className='ob-storage-usage__bar-fill' style={{width: `${percent}%`}} />
      </div>
      <dl className='ob-storage-usage__facts'>
        <dt>Limit</dt>
        <dd>{formatBytes(STORAGE_LIMIT_BYTES)}</dd>
        <dt>Available</dt>
        <dd>{formatBytes(Math.max(STORAGE_LIMIT_BYTES - usage, 0))}</dd>
        <dt>In use</dt>
        <dd>{percentLabel}</dd>
      </dl>
    </div>
  )
}

/**
 * The storage icon in the Files panel header: opens a box, styled as Transfers', with how much of
 * the cloud storage is used. Refetches the usage each time it opens.
 */
export const StorageUsageDropdown: React.FC = () => {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const {data, isLoading, isError, refetch} = useStorageUsage()
  const close = useCallback(() => setOpen(false), [])
  useDismissOnOutside(open, close, dropdownRef, triggerRef)

  const label = data === undefined ? 'Storage usage' : `Storage usage: ${formatBytes(data)}`

  const toggle = () => {
    if (!open) void refetch()
    setOpen((prev) => !prev)
  }

  return (
    <div className='ob-files-panel__dropdown ob-storage-usage__dropdown' ref={dropdownRef}>
      <button
        ref={triggerRef}
        type='button'
        className={`ob-files-panel__icon-btn${open ? ' active' : ''}`}
        onClick={toggle}
        title={label}
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? MENU_ID : undefined}
        data-testid='omics-files-usage'
      >
        <i className='fas fa-hdd' />
      </button>
      {open && (
        <div
          id={MENU_ID}
          className='ob-files-panel__dropdown-menu ob-storage-usage__menu'
          role='region'
          aria-label='Storage'
        >
          <div className='ob-storage-usage__title'>Storage</div>
          {data !== undefined ? (
            <UsageDetails usage={data} />
          ) : (
            <div className='ob-storage-usage__empty'>
              {isError
                ? 'Could not load storage usage.'
                : isLoading
                ? 'Loading…'
                : 'No usage data.'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
